import { translate } from '~/composables/translate/translations'
import { usePersonalStore } from '~/stores/personal'

/**
 * Validation rules for new-user registration fields collected during pre-booking.
 * Based on the same rules used in customer-registration-ui and customer-onboarding-ui.
 * CCT-2060
 */

// Name: broad character set to support European names (from customer-onboarding-ui)
const NAME_PATTERN = /^[\w\W@#$&()\-:/.,\"' àáąâãäåæçćéęèêëíìîïłńñóøòôõöśßùúûüÿýźżÀÁĄÂÃÄÅÆÇĆÉÈÊĘËÌÍÎÏŁÑŃÓÒÔÕÖØŚÜÚÛÙÝŸŹŻ]+$/

// Password: align with customer-registration-ui
// 8-25 chars, at least 1 uppercase, 1 lowercase, 1 digit, 1 special char
// Special chars: !@#$%^&*)(+=._-: (colon added per CCT-2060 requirement)
const PASSWORD_MIN = 8
const PASSWORD_MAX = 25
const PASSWORD_SPECIAL = /[!@#$%^&*)(+=._\-:]/

// ZIP code patterns per country (from customer-onboarding-ui)
const ZIP_PATTERNS: Record<string, RegExp> = {
  DE: /^\d{5}$/,
  AT: /^\d{4}$/,
  FR: /^\d{2} ?\d{3}$/,
  IT: /^\d{5}$/,
  ES: /^\d{5}$/,
  NL: /^[1-9]\d{3}[ ]?[A-Z]{2}$/i,
  BE: /^\d{4}$/,
  CH: /^\d{4}$/,
  PL: /^\d{2}-\d{3}$/,
  DK: /^\d{4}$/,
  SE: /^\d{3} ?\d{2}$/,
  NO: /^\d{4}$/,
  PT: /^\d{4}-\d{3}$/,
  HU: /^\d{4}$/,
  GB: /^[A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2}$/i,
  US: /^(\d{5})(?:[ -](\d{4}))?$/,
  CA: /^[ABCEGHJKLMNPRSTVXY]\d[ABCEGHJ-NPRSTV-Z] ?\d[ABCEGHJ-NPRSTV-Z]\d$/i,
}

export interface PrebookingValidationErrors {
  addressStreet?: string
  addressCity?: string
  addressZip?: string
  password?: string
}

export function usePrebookingValidation() {
  const personalStore = usePersonalStore()

  function validateNewUserFields(): PrebookingValidationErrors {
    const errors: PrebookingValidationErrors = {}
    const country = personalStore.countryOfResidence?.toUpperCase() ?? ''

    // Street
    const street = personalStore.addressStreet?.trim() ?? ''
    if (!street) {
      errors.addressStreet = translate('error_required_field')
    }
    else if (!NAME_PATTERN.test(street)) {
      errors.addressStreet = translate('prebooking_error_invalid_address')
    }
    else if (street.length > 100) {
      errors.addressStreet = translate('prebooking_error_address_too_long')
    }

    // City
    const city = personalStore.addressCity?.trim() ?? ''
    if (!city) {
      errors.addressCity = translate('error_required_field')
    }
    else if (!NAME_PATTERN.test(city)) {
      errors.addressCity = translate('prebooking_error_invalid_city')
    }
    else if (city.length > 100) {
      errors.addressCity = translate('prebooking_error_city_too_long')
    }

    // ZIP — country-specific pattern, falls back to non-empty check
    const zip = personalStore.addressZip?.trim() ?? ''
    if (!zip) {
      errors.addressZip = translate('error_required_field')
    }
    else {
      const zipPattern = ZIP_PATTERNS[country]
      if (zipPattern && !zipPattern.test(zip)) {
        errors.addressZip = translate('prebooking_error_invalid_zip')
      }
    }

    // Password
    const password = personalStore.password ?? ''
    if (!password) {
      errors.password = translate('error_required_field')
    }
    else if (password.length < PASSWORD_MIN) {
      errors.password = translate('prebooking_error_password_too_short')
    }
    else if (password.length > PASSWORD_MAX) {
      errors.password = translate('prebooking_error_password_too_long')
    }
    else if (!/[a-z]/.test(password)) {
      errors.password = translate('prebooking_error_password_no_lowercase')
    }
    else if (!/[A-Z]/.test(password)) {
      errors.password = translate('prebooking_error_password_no_uppercase')
    }
    else if (!/\d/.test(password)) {
      errors.password = translate('prebooking_error_password_no_number')
    }
    else if (!PASSWORD_SPECIAL.test(password)) {
      errors.password = translate('prebooking_error_password_no_special')
    }

    return errors
  }

  function hasNewUserErrors(errors: PrebookingValidationErrors): boolean {
    return Object.values(errors).some(e => !!e)
  }

  return {
    validateNewUserFields,
    hasNewUserErrors,
  }
}
